import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Group, Pagination, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconChevronRight, IconFlask, IconMapPin, IconScale, IconWheat } from '@tabler/icons-react';
import BestPriceLabel from '@/components/common/BestPriceLabel';
import MetaChip from '@/components/common/MetaChip';
import PrepaymentBadge from '@/components/common/PrepaymentBadge';
import { offerQualityKey, useOffersQuality } from '@/hooks/useBestOffers';
import { bestOfferLink, bestPriceTone } from '@/lib/bestOffer';
import { formatOrgName, formatProperty, formatWeightTon } from '@/lib/format';
import type { MyBestOffer } from '@/types/bestPrice';
import classes from './homeRow.module.css';

interface BestOffersListProps {
  readonly items: readonly MyBestOffer[];
}

const PAGE_SIZE = 6;

// Виджет «Лучшие предложения для вас»: лучшие встречные цены по моим прайсам/офферам (обе стороны).
// Цена + оценка выгоды — единый вид BestPriceLabel. Список может быть длинным → пагинация по PAGE_SIZE.
// Клик по строке → страница встречного предложения.
export default function BestOffersList({ items }: BestOffersListProps) {
  const [page, setPage] = useState(1);
  // Главный параметр качества (протеин и т.п.) по каждому офферу — резолвится из словаря свойств.
  const qualityMap = useOffersQuality(items);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  // Клампим страницу: после refetch список мог сократиться (иначе пустая страница вне диапазона).
  const safePage = Math.min(page, totalPages);
  const pageItems = items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <Card withBorder padding={0}>
      <Stack gap={0}>
        {pageItems.map((offer) => {
          // Сторона позиции (иконка): продажа — brand, закупка — lime (фирменная триада).
          const sideColor = offer.side === 'SELL' ? 'brand' : 'lime';
          // Заголовок — только товар + класс (goodsType). Качество, объём, базис, предоплата — чипами ниже.
          const title = offer.goodsType ?? '—';
          // Главный параметр качества (напр. «Протеин ≥ 13.0 %») по набору свойств оффера.
          const quality = qualityMap.get(offerQualityKey(offer.goodsType, offer.propertySetId));
          return (
            <UnstyledButton
              key={`${offer.side}-${offer.myOrderId}`}
              component={Link}
              to={bestOfferLink(offer)}
              className={classes.row}
            >
              {/* Слева: заголовок + ряд чипов (объём · базис · предоплата), справа 2-строчная кнопка-цена.
                  Выравниваем по верху; иконку/шеврон центрируем по строке отдельно (alignSelf: center). */}
              <Group align="flex-start" wrap="nowrap" gap="sm">
                <ThemeIcon variant="light" color={sideColor} size="md" radius="xl" style={{ alignSelf: 'center' }}>
                  <IconWheat size={16} />
                </ThemeIcon>

                <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={600} size="sm" truncate>
                    {title}
                  </Text>
                  {/* Чипы метаданных: качество/объём/базис — нейтральные, предоплата — зелёный акцент. Переносятся. */}
                  <Group gap="xs" wrap="wrap">
                    {quality ? (
                      <MetaChip icon={<IconFlask size={13} />}>{formatProperty(quality)}</MetaChip>
                    ) : null}
                    {offer.weightTon != null ? (
                      <MetaChip icon={<IconScale size={13} />}>{formatWeightTon(offer.weightTon)}</MetaChip>
                    ) : null}
                    {offer.addressTitle ? (
                      <MetaChip icon={<IconMapPin size={13} />}>{formatOrgName(offer.addressTitle)}</MetaChip>
                    ) : null}
                    <PrepaymentBadge value={offer.prepaymentType} compact />
                  </Group>
                </Stack>

                {/* Колонка цены — фиксированной ширины, кнопки прижаты к правому краю колонки. block у
                    BestPriceLabel даёт кнопкам единую ширину (min-width под самую длинную) с текстом по центру;
                    ширина колонки 200 вмещает дельту «+12 233 к вашей цене» (до 6 знаков). Строки — как в таблице. */}
                <Stack gap={0} align="flex-end" w={200} style={{ flexShrink: 0 }}>
                  {offer.bestPrice != null ? (
                    <BestPriceLabel
                      price={offer.bestPrice}
                      action={offer.side === 'SELL' ? 'sell' : 'buy'}
                      tone={bestPriceTone(offer.advantage)}
                      advantage={offer.advantage}
                      block
                    />
                  ) : null}
                </Stack>

                <IconChevronRight size={16} color="var(--mantine-color-gray-5)" style={{ flexShrink: 0, alignSelf: 'center' }} />
              </Group>
            </UnstyledButton>
          );
        })}
      </Stack>
      {totalPages > 1 ? (
        <Group justify="center" p="sm">
          {/* Активная страница — фирменный зелёный (brand), а не фиолетовый. */}
          <Pagination value={safePage} onChange={setPage} total={totalPages} size="sm" color="brand" />
        </Group>
      ) : null}
    </Card>
  );
}
